import { createApi } from "@reduxjs/toolkit/query/react";
import { contractsApi } from "./contractsApi";
import { baseQueryWithAuthHandling } from "./baseQuery";
import type {
  LegalEntityLookupRow,
  PaginatedLookupResult,
  PaginatedGroupedUnmatchedResult,
  CreateLookupPayload,
  UpdateLookupPayload,
  MapGroupedPayload,
  GroupedActionPayload,
  LegalEntityOption,
  SourceOption,
  CreateLegalEntityPayload,
  CreatedLegalEntityRow,
  MappingsSummary,
  CreateLookupResult,
  UpdateLookupResult,
} from "../types/legalEntityLookup";

export interface LookupListParams {
  page: number;
  pageSize: number;
  search?: string;
  sourceKey?: string;
  status?: "active" | "inactive" | "all";
}

export interface UnmatchedListParams {
  page: number;
  pageSize: number;
  search?: string;
  sourceKey?: string;
  status?: "Open" | "Ignored" | "All";
}

function buildQuery(
  base: string,
  params: Record<string, string | number | undefined>,
): string {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");
  return qs ? `${base}?${qs}` : base;
}

export const legalEntityLookupApi = createApi({
  reducerPath: "legalEntityLookupApi",
  baseQuery: baseQueryWithAuthHandling,
  tagTypes: [
    "LookupList",
    "UnmatchedList",
    "LegalEntityOptions",
    "SourceOptions",
    "MappingsSummary",
  ],
  endpoints: (builder) => ({
    getLegalEntityLookups: builder.query<
      PaginatedLookupResult,
      LookupListParams
    >({
      query: ({ page, pageSize, search, sourceKey, status }) =>
        buildQuery("/legal-entity-lookups", {
          page,
          pageSize,
          search,
          sourceKey,
          status,
        }),
      transformResponse: (raw: {
        status: string;
        data: PaginatedLookupResult;
      }) => raw.data,
      providesTags: ["LookupList"],
    }),
    createLegalEntityLookup: builder.mutation<
      CreateLookupResult,
      CreateLookupPayload
    >({
      query: (body) => ({ url: "/legal-entity-lookups", method: "POST", body }),
      transformResponse: (raw: { status: string; data: CreateLookupResult }) =>
        raw.data,
      invalidatesTags: [
        "LookupList",
        "UnmatchedList",
        "LegalEntityOptions",
        "MappingsSummary",
      ],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(contractsApi.util.invalidateTags(["NotificationSummary"]));
      },
    }),
    updateLegalEntityLookup: builder.mutation<
      UpdateLookupResult,
      UpdateLookupPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/legal-entity-lookups/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (raw: { status: string; data: UpdateLookupResult }) =>
        raw.data,
      invalidatesTags: ["LookupList", "MappingsSummary"],
    }),
    disableLegalEntityLookup: builder.mutation<LegalEntityLookupRow, number>({
      query: (id) => ({
        url: `/legal-entity-lookups/${id}/disable`,
        method: "PATCH",
      }),
      transformResponse: (raw: {
        status: string;
        data: LegalEntityLookupRow;
      }) => raw.data,
      invalidatesTags: ["LookupList", "MappingsSummary"],
    }),
    enableLegalEntityLookup: builder.mutation<LegalEntityLookupRow, number>({
      query: (id) => ({
        url: `/legal-entity-lookups/${id}/enable`,
        method: "PATCH",
      }),
      transformResponse: (raw: {
        status: string;
        data: LegalEntityLookupRow;
      }) => raw.data,
      invalidatesTags: ["LookupList", "MappingsSummary"],
    }),
    // Grouped unmatched — returns GroupedUnmatchedRow[]
    getUnmatchedLegalEntityLookups: builder.query<
      PaginatedGroupedUnmatchedResult,
      UnmatchedListParams
    >({
      query: ({ page, pageSize, search, sourceKey, status }) =>
        buildQuery("/unmatched-legal-entity-lookups", {
          page,
          pageSize,
          search,
          sourceKey,
          status,
        }),
      transformResponse: (raw: {
        status: string;
        data: PaginatedGroupedUnmatchedResult;
      }) => raw.data,
      providesTags: ["UnmatchedList"],
    }),
    ignoreGroupedUnmatchedLookup: builder.mutation<
      { normalizedKey: string; status: string },
      GroupedActionPayload
    >({
      query: (body) => ({
        url: "/unmatched-legal-entity-lookups/ignore",
        method: "POST",
        body,
      }),
      transformResponse: (raw: {
        status: string;
        data: { normalizedKey: string; status: string };
      }) => raw.data,
      invalidatesTags: ["UnmatchedList", "MappingsSummary"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(contractsApi.util.invalidateTags(["NotificationSummary"]));
      },
    }),
    reopenGroupedUnmatchedLookup: builder.mutation<
      { normalizedKey: string; status: string },
      GroupedActionPayload
    >({
      query: (body) => ({
        url: "/unmatched-legal-entity-lookups/reopen",
        method: "POST",
        body,
      }),
      transformResponse: (raw: {
        status: string;
        data: { normalizedKey: string; status: string };
      }) => raw.data,
      invalidatesTags: ["UnmatchedList", "MappingsSummary"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(contractsApi.util.invalidateTags(["NotificationSummary"]));
      },
    }),
    mapGroupedUnmatchedLookup: builder.mutation<
      LegalEntityLookupRow[],
      MapGroupedPayload
    >({
      query: (body) => ({
        url: "/unmatched-legal-entity-lookups/map",
        method: "POST",
        body,
      }),
      transformResponse: (raw: {
        status: string;
        data: LegalEntityLookupRow[];
      }) => raw.data,
      invalidatesTags: ["LookupList", "UnmatchedList", "MappingsSummary"],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => {});
        dispatch(contractsApi.util.invalidateTags(["NotificationSummary"]));
      },
    }),
    getLegalEntityOptions: builder.query<LegalEntityOption[], void>({
      query: () => "/legal-entity-options",
      transformResponse: (raw: { status: string; data: LegalEntityOption[] }) =>
        raw.data,
      providesTags: ["LegalEntityOptions"],
    }),
    getSourceOptions: builder.query<SourceOption[], void>({
      query: () => "/legal-entity-mappings/source-options",
      transformResponse: (raw: { status: string; data: SourceOption[] }) =>
        raw.data,
      providesTags: ["SourceOptions"],
    }),
    createLegalEntity: builder.mutation<
      CreatedLegalEntityRow,
      CreateLegalEntityPayload
    >({
      query: (body) => ({ url: "/legal-entities", method: "POST", body }),
      transformResponse: (raw: {
        status: string;
        data: CreatedLegalEntityRow;
      }) => raw.data,
      invalidatesTags: ["LegalEntityOptions"],
    }),
    getMappingsSummary: builder.query<MappingsSummary, void>({
      query: () => "/legal-entity-mappings/summary",
      transformResponse: (raw: { status: string; data: MappingsSummary }) =>
        raw.data,
      providesTags: ["MappingsSummary"],
    }),
  }),
});

export const {
  useGetLegalEntityLookupsQuery,
  useCreateLegalEntityLookupMutation,
  useUpdateLegalEntityLookupMutation,
  useDisableLegalEntityLookupMutation,
  useEnableLegalEntityLookupMutation,
  useGetUnmatchedLegalEntityLookupsQuery,
  useIgnoreGroupedUnmatchedLookupMutation,
  useReopenGroupedUnmatchedLookupMutation,
  useMapGroupedUnmatchedLookupMutation,
  useGetLegalEntityOptionsQuery,
  useGetSourceOptionsQuery,
  useCreateLegalEntityMutation,
  useGetMappingsSummaryQuery,
} = legalEntityLookupApi;
